import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Lightbulb, Target, Flag, Building2, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { createPageUrl } from '@/utils';
import { notify } from '@/components/ui/notify';
import { buildPersonasTabPagePath, isPlanResearchItem, planItemKey, planItemWhy } from '@/lib/todaysPlanUtils';
import { openRecommendationComposeDraft, RecommendationComposeNotFoundError, resolveRecommendationId } from '@/lib/chatbotBrief';
import { buildComposeViaPersonaPagePath } from '@/lib/chatbotDeepLinks';
import PersonAvatar from '@/components/PersonAvatar';
import { resolveDealId } from '@/lib/api/myTenders';
import PilotTile, { TileEmpty, TileSkeleton } from './PilotTile';
import DealStatusMenu from './DealStatusMenu';
import { DeadlineBadge, AccountLogo } from './pilotBadges';
import { markOnce } from './pilotPerf';

const API_BASE_URL = 'https://api.neogtm.com';
const LIMIT = 5;
const HOME_DEAL_SLOTS = 2;

const DEAL_RULE_COPY = {
  record_outcome: { verb: 'Record outcome', button: 'Record outcome', Icon: Flag },
  mark_submitted: { verb: 'Mark submitted', button: 'Update status', Icon: Target },
  set_status: { verb: 'Set status', button: 'Set status', Icon: Target },
};

const CHANNEL_LABEL = { email: 'Email', linkedin: 'LinkedIn', call: 'Call' };
const BTN = 'h-8 rounded-md border border-gray-300 bg-white px-3 text-xs font-medium text-[#0A1A2F] hover:bg-gray-50';
const BTN_QUIET = 'h-8 rounded-md px-2 text-xs font-medium text-[#5C6B7A] hover:bg-white/60';

/** Same cleanup as ActionCenterWithPlan: drop the "high fit (...)" and decision-window tails. */
function cleanWhyNow(text) {
  if (!text) return text;
  return String(text)
    .replace(/;\s*high fit\s*\([^)]+\)/i, '')
    .replace(/;\s*decision window\s*[><]\s*\d+\s*months?\.?/i, '')
    .trim();
}

function IconBox({ Icon }) {
  return (
    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 text-[#5C6B7A]">
      <Icon className="w-4 h-4" aria-hidden="true" />
    </div>
  );
}

function DueBadge({ tone, children }) {
  const cls = {
    red: 'bg-red-50 text-red-600 border-red-200',
    orange: 'bg-orange-100 text-orange-800 border-orange-200',
    grey: 'bg-gray-100 text-gray-600 border-gray-200',
  }[tone];
  return <Badge variant="outline" className={`text-[10px] h-5 px-1.5 shrink-0 whitespace-nowrap border ${cls}`}>{children}</Badge>;
}

function findPerson(people, id, name) {
  if (!people?.length) return null;
  if (id) {
    const hit = people.find((p) => p.id === id || p.person_id === id);
    if (hit) return hit;
  }
  if (name) {
    const n = String(name).toLowerCase().trim();
    return people.find((p) => `${p.first_name || ''} ${p.last_name || ''}`.toLowerCase().trim() === n) || null;
  }
  return null;
}

function Avatar({ person, name, personId }) {
  const [first, ...rest] = String(name || '').split(' ');
  return (
    <PersonAvatar
      personId={person?.person_id || person?.id || personId}
      hostedPhotoUrl={person?.hosted_photo_url}
      linkedinPhotoUrl={person?.profile_photo_url}
      firstName={person?.first_name || first}
      lastName={person?.last_name || rest.join(' ')}
      size="sm"
    />
  );
}

/**
 * Tasks: the deal rules (lib/homeDealTasks) first — they carry deadlines — then outreach due
 * today, then Today's plan recommendations. Persona tasks show the person; the rest an icon.
 */
/** The tenant's own account row for a deal or plan item — that row carries the logo. */
function findAccount(accounts, tenantId, { accountId, name }) {
  if (!accounts?.length) return null;
  if (accountId) {
    const prefixed = String(accountId).startsWith(`${tenantId}_`) ? accountId : `${tenantId}_${accountId}`;
    const hit = accounts.find((a) => a.id === accountId || a.account_id === accountId
      || a.account_id === prefixed || a.global_pool_account_id === accountId);
    if (hit) return hit;
  }
  if (name) {
    const n = String(name).toLowerCase().trim();
    return accounts.find((a) => String(a.account_name || '').toLowerCase().trim() === n) || null;
  }
  return null;
}

export default function TasksTile({ tenantId, userId, deals, plan, people, accounts, onOpenDraft, onAcknowledge, full = false, planLoading = false }) {
  const navigate = useNavigate();
  const [outreach, setOutreach] = useState(null);
  const [draftingKey, setDraftingKey] = useState(null);
  const [completed, setCompleted] = useState(() => new Set());

  useEffect(() => {
    if (!tenantId || !userId) { setOutreach([]); return undefined; }
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/todays-plan/tasks?tenant_id=${encodeURIComponent(tenantId)}&user_id=${encodeURIComponent(userId)}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => { if (!cancelled) { setOutreach(Array.isArray(data) ? data : []); markOnce('tasks_outreach'); } })
      .catch(() => !cancelled && setOutreach([]));
    return () => { cancelled = true; };
  }, [tenantId, userId]);

  const items = useMemo(() => {
    // Order: deals already late or about to be (outcome overdue, bid not marked submitted), then
    // outreach due today, then deals still needing a first status, then plan recommendations.
    const dealTasks = deals.dealTasks || [];
    const urgentDeals = dealTasks.filter((d) => d.rule !== 'set_status');
    const statusDeals = dealTasks.filter((d) => d.rule === 'set_status');
    return [
      ...urgentDeals.map((task) => ({ kind: 'deal', key: task.id, task })),
      ...(outreach || []).filter((task) => !completed.has(task.id)).map((task) => ({ kind: 'outreach', key: `outreach:${task.id}`, task })),
      ...statusDeals.map((task) => ({ kind: 'deal', key: task.id, task })),
      ...(plan || []).map((item) => ({
        kind: 'plan',
        key: `plan:${item.recommendation_id || `${item.accountId}:${item.personaId}:${item.title}`}`,
        item,
      })),
    ];
  }, [deals.dealTasks, outreach, plan, completed]);

  // Plan items are most of what Tasks shows for many tenants: while the plan is still loading and
  // there are no plan items yet, show the skeleton rather than "Nothing due".
  const loading = deals.loading || outreach == null
    || (planLoading && !(plan || []).length && !(deals.dealTasks || []).length && !(outreach || []).length);
  /* Home shows at most HOME_DEAL_SLOTS deal tasks (the most urgent) and fills the rest with people,
   * so the tile is never all procurement (Tiago 2026-11-04); deals fill any slots people leave empty.
   * The Tasks page shows everything in its normal order. */
  const visible = useMemo(() => {
    if (full) return items;
    const dealItems = items.filter((e) => e.kind === 'deal');
    const otherItems = items.filter((e) => e.kind !== 'deal');
    const deals = dealItems.slice(0, HOME_DEAL_SLOTS);
    const others = otherItems.slice(0, LIMIT - deals.length);
    const fill = dealItems.slice(deals.length, deals.length + (LIMIT - deals.length - others.length));
    const picked = new Set([...deals, ...others, ...fill]);
    return items.filter((e) => picked.has(e));
  }, [items, full]);

  /* Same as Today's plan "Open Draft" (ActionCenterWithPlan.handleOpenPlanDraft): a recommendation
   * with an id opens its prepared compose draft; otherwise the persona page opens with a brief and
   * auto_generate=1, so the message is drafted there on arrival. */
  const openPlanDraft = async (item, { finalPersonId, accountIdForLink, why }) => {
    if (!userId || !tenantId) return;
    const key = planItemKey(item);
    /* Go straight to the persona page when the task names its person: the brief is built from what
     * the task already carries (title, why now, next steps), so there is no round trip before the
     * page changes — the draft then generates on the persona page (auto_generate=1). Only a task
     * with no person waits for the server to resolve the recommendation's target. */
    const steps = Array.isArray(item.steps) ? item.steps.join(' ') : item.nextSteps;
    // why_now and the cleaned why line are often the same sentence; say it once.
    const composeBrief = [...new Set([item.title, item.why_now, why, steps].filter(Boolean).map((x) => String(x).trim()))]
      .join('. ').trim();
    if (finalPersonId) {
      navigate(createPageUrl(buildComposeViaPersonaPagePath({
        personId: finalPersonId, accountId: accountIdForLink, composeBrief, autoGenerate: true,
      })));
      return;
    }
    setDraftingKey(key);
    try {
      const recommendationId = resolveRecommendationId(item);
      let pagePath = null;
      if (recommendationId) {
        pagePath = await openRecommendationComposeDraft({ tenantId, userId, recommendationId });
      } else {
        pagePath = buildComposeViaPersonaPagePath({ personId: finalPersonId, accountId: accountIdForLink, composeBrief, autoGenerate: true });
      }
      if (!pagePath) throw new Error('No persona target for this recommendation');
      navigate(createPageUrl(pagePath));
    } catch (err) {
      const notFound = err instanceof RecommendationComposeNotFoundError || err?.status === 404;
      notify({
        title: 'Could not open the draft',
        description: notFound ? 'No contact on file for this one. Open the account\'s personas instead.' : (err?.message || 'Please try again'),
        variant: 'destructive',
      });
    } finally {
      setDraftingKey(null);
    }
  };

  // Same endpoint as Today's plan "Done" on a due touch.
  const completeOutreach = async (task) => {
    if (!userId || !task?.id) return;
    setCompleted((s) => new Set(s).add(task.id));
    try {
      const res = await fetch(`${API_BASE_URL}/api/todays-plan/tasks/${encodeURIComponent(task.id)}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tenant_id: tenantId, user_id: userId, description: "Completed from Today's plan" }),
      });
      if (!res.ok) throw new Error(`Could not mark done (${res.status})`);
    } catch (err) {
      setCompleted((s) => { const n = new Set(s); n.delete(task.id); return n; });
      notify({ title: 'Could not mark the task done', description: err?.message, variant: 'destructive' });
    }
  };

  const renderRow = (entry) => {
    if (entry.kind === 'deal') {
      const { task } = entry;
      const copy = DEAL_RULE_COPY[task.rule];
      const id = resolveDealId(task.deal);
      return (
        <>
          {(() => {
            const acc = findAccount(accounts, tenantId, { accountId: task.deal.account_id, name: task.deal.institution });
            return <AccountLogo url={acc?.logo_url} name={task.deal.institution} fallback={<IconBox Icon={copy.Icon} />} />;
          })()}
          <div className="min-w-0 flex-1">
            <p className="line-clamp-2 text-sm font-medium leading-snug text-[#0A1A2F]" title={task.deal.title || ''}>{copy.verb} · {task.deal.title || 'Untitled tender'}</p>
            <div className="mt-1 flex min-w-0 items-center gap-1.5">
              {task.rule === 'record_outcome' ? <DueBadge tone="red">Overdue</DueBadge> : <DeadlineBadge deal={task.deal} />}
              <span className="min-w-0 truncate text-xs text-[#5C6B7A]">{task.deal.institution || '—'}</span>
            </div>
          </div>
          <div className="basis-full pl-11 sm:basis-auto sm:pl-0 flex-shrink-0">
          <DealStatusMenu
            dealId={id}
            status={deals.augment[id]?.status || null}
            onChange={deals.changeStatus}
            saving={deals.savingId === id}
            triggerLabel={copy.button}
          />
          </div>
        </>
      );
    }
    if (entry.kind === 'outreach') {
      const { task } = entry;
      const person = findPerson(people, task.person_id, task.person_name);
      const channel = CHANNEL_LABEL[task.channel] || 'Touch';
      const canOpen = task.channel === 'email' || (task.channel === 'linkedin' && task.linkedin_url) || (task.channel === 'call' && task.phone);
      return (
        <>
          <Avatar person={person} name={task.person_name} personId={task.person_id} />
          <div className="min-w-0 flex-1">
            <p className="line-clamp-2 text-sm font-medium leading-snug text-[#0A1A2F]">{task.person_name} · {channel}{task.touch_number ? `, touch ${task.touch_number}` : ''}</p>
            <div className="mt-1 flex min-w-0 items-center gap-1.5">
              <DueBadge tone="orange">Today</DueBadge>
              <span className="min-w-0 truncate text-xs text-[#5C6B7A]">{task.account_name || '—'}</span>
            </div>
          </div>
          <div className="flex flex-shrink-0 gap-1.5 basis-full pl-11 sm:basis-auto sm:pl-0">
            {canOpen ? (
              <button type="button" onClick={() => onOpenDraft?.(task.step_id, task)} className={BTN}>
                {task.channel === 'email' ? 'Open draft' : task.channel === 'linkedin' ? 'Open LinkedIn' : 'Call'}
              </button>
            ) : null}
            <button type="button" onClick={() => completeOutreach(task)} className={BTN_QUIET}>Done</button>
          </div>
        </>
      );
    }
    const { item } = entry;
    const person = item.personaName ? findPerson(people, item.personaId, item.personaName) : null;
    const finalPersonId = person?.person_id || person?.id || item.personaId || item.person_id || null;
    const account = findAccount(accounts, tenantId, { accountId: item.accountId || item.account_id, name: item.accountName });
    const accountIdForLink = item.accountId || item.account_id || account?.account_id || account?.id || null;
    const why = cleanWhyNow(planItemWhy(item));
    const research = isPlanResearchItem(item);
    const key = planItemKey(item);
    const canDraft = Boolean(finalPersonId || accountIdForLink || resolveRecommendationId(item));
    return (
      <>
        {item.personaName ? <Avatar person={person} name={item.personaName} personId={item.personaId} /> : (
          <AccountLogo url={account?.logo_url} name={item.accountName} fallback={<IconBox Icon={Building2} />} />
        )}
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 text-sm font-medium leading-snug text-[#0A1A2F]" title={item.title || ''}>
            {item.personaName ? `${item.personaName} · ` : ''}{item.title}
          </p>
          {why ? <p className="mt-0.5 line-clamp-2 text-xs leading-snug text-[#5C6B7A]" title={why}>{why}</p> : null}
          <div className="mt-1 flex min-w-0 items-center gap-1.5">
            <DueBadge tone="grey">{item.category || 'Plan'}</DueBadge>
            <span className="min-w-0 truncate text-xs text-[#5C6B7A]">{item.accountName || '—'}</span>
          </div>
        </div>
        <div className="flex flex-shrink-0 gap-1.5 basis-full pl-11 sm:basis-auto sm:pl-0">
          {research ? (
            <button type="button" onClick={() => navigate(createPageUrl(buildPersonasTabPagePath(accountIdForLink)))} className={BTN}>
              Open personas
            </button>
          ) : canDraft ? (
            <button
              type="button"
              disabled={draftingKey === key}
              onClick={() => openPlanDraft(item, { finalPersonId, accountIdForLink, why })}
              className={`${BTN} inline-flex items-center gap-1 disabled:opacity-60`}
            >
              {draftingKey === key ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" /> : null}
              Open draft
            </button>
          ) : null}
          {onAcknowledge ? (
            <button type="button" onClick={() => onAcknowledge({ ...item, person_id: item.person_id || finalPersonId })} className={BTN_QUIET}>
              Done
            </button>
          ) : null}
        </div>
      </>
    );
  };

  return (
    <PilotTile
      testId="pilot-tile-tasks"
      icon={Lightbulb}
      title="Tasks"
      href={full ? undefined : createPageUrl('Tasks')}
      onTitleClick={full ? () => {} : undefined}
      noChevron={full}
      count={loading ? null : items.length}
      highlight
    >
      {loading ? (
        <TileSkeleton />
      ) : items.length === 0 ? (
        <TileEmpty>Nothing due. New tasks appear here as deadlines and outreach come up.</TileEmpty>
      ) : (
        <>
          <div className="flex flex-col gap-2">
            {visible.map((entry) => (
              <div key={entry.key} className="flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-2 rounded-lg border border-gray-200 bg-white px-2.5 py-2 shadow-sm">
                {renderRow(entry)}
              </div>
            ))}
          </div>
          {!full && items.length > LIMIT ? (
            <Link to={createPageUrl('Tasks')} className="mt-1 self-start text-xs font-medium text-[#00C2C2] hover:text-[#00A3A3]">
              View all {items.length}
            </Link>
          ) : null}
        </>
      )}
    </PilotTile>
  );
}
